/**
 * Databases API Service
 * Comunicação com endpoints de databases
 */

import type { AxiosRequestConfig } from 'axios';
import { api } from '@/lib/api';
import type {
  DatabaseResponse,
  CreateDatabaseRequest,
  DatabaseQuery,
  CreateDatabaseDocumentRequest,
  DatabaseDocumentResponse,
  DatabaseDocumentTransferRequest,
  DatabaseDocumentTransferResult,
} from '@troia-v3/types';

// Create/update são tratados localmente pelos form pages (banner + destaque de
// campos via extractApiFieldErrors) — suprime o toast global cru do interceptor.
const HANDLE_ERROR_LOCALLY: AxiosRequestConfig & { _handleErrorLocally: boolean } = {
  _handleErrorLocally: true,
};

export const databasesApi = {
  /**
   * Criar nova base de dados
   */
  async create(data: CreateDatabaseRequest): Promise<DatabaseResponse> {
    const response = await api.post('databases', data);
    return response.data.data;
  },

  /**
   * Listar bases de dados
   */
  async getAll(query?: DatabaseQuery): Promise<{
    items: DatabaseResponse[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const response = await api.get('databases', { params: query });
    return response.data.data;
  },

  /**
   * Obter base de dados por ID
   */
  async getById(id: string): Promise<DatabaseResponse> {
    const response = await api.get(`databases/${id}`);
    return response.data.data;
  },

  /**
   * Atualizar base de dados
   */
  async update(id: string, data: Partial<CreateDatabaseRequest>): Promise<DatabaseResponse> {
    const response = await api.patch(`databases/${id}`, data);
    return response.data.data;
  },

  /**
   * Deletar base de dados
   */
  async delete(id: string): Promise<void> {
    await api.delete(`databases/${id}`);
  },

  /**
   * Criar documento
   * Rota backend: POST /api/databases/documents
   */
  async createDocument<T = unknown>(data: CreateDatabaseDocumentRequest<T>): Promise<DatabaseDocumentResponse<T>> {
    const response = await api.post('databases/documents', data, HANDLE_ERROR_LOCALLY);
    return response.data.data;
  },

  /**
   * Copiar registros para outra base. "Duplicar aqui" é esta mesma rota com
   * `targetDatabaseId` igual à base atual (spec §2).
   * Rota backend: POST /api/databases/documents/copy — permissão `databases:create`
   *
   * Devolve 200 com o desfecho item a item mesmo com falha parcial: quem chama
   * NÃO deve tratar `rejected > 0` como erro de rede. O erro que chega no
   * `catch` é o do lote inteiro (404 da base de destino, 422 de lista vazia ou
   * acima de 50) — e vem com `_handleErrorLocally` porque o modal é quem
   * escreve a mensagem (senão sai toast duplicado).
   */
  async copyDocuments(data: DatabaseDocumentTransferRequest): Promise<DatabaseDocumentTransferResult> {
    const response = await api.post('databases/documents/copy', data, HANDLE_ERROR_LOCALLY);
    return response.data.data;
  },

  /**
   * Mover registros para outra base — mesmo `_id`, só troca de base.
   * Rota backend: POST /api/databases/documents/move — permissão `databases:update`
   *
   * Mesmo corpo e mesmo formato de resposta do `copyDocuments`; o status do
   * item é `moved` | `rejected`.
   */
  async moveDocuments(data: DatabaseDocumentTransferRequest): Promise<DatabaseDocumentTransferResult> {
    const response = await api.post('databases/documents/move', data, HANDLE_ERROR_LOCALLY);
    return response.data.data;
  },

  /**
   * Obter documento por ID
   * Rota backend: GET /api/databases/documents/:id
   */
  async getDocumentById<T = unknown>(documentId: string): Promise<DatabaseDocumentResponse<T>> {
    const response = await api.get(`databases/documents/${documentId}`);
    return response.data.data;
  },

  /**
   * Trilhas já usadas na base (para o formulário sugerir nomes existentes)
   * Rota backend: GET /api/databases/:id/documents/series
   */
  async getDocumentSeries(databaseId: string): Promise<Array<{ name: string; count: number }>> {
    const response = await api.get(`databases/${databaseId}/documents/series`);
    return response.data.data;
  },

  /**
   * Exporta a ficha do imóvel em PDF (geração pode levar ~10-30s na 1ª vez)
   * Rota backend: GET /api/databases/documents/:id/export/pdf?variant=client|internal
   */
  async exportPropertyPdf(documentId: string, variant: 'client' | 'internal'): Promise<Blob> {
    const config: AxiosRequestConfig & { _handleErrorLocally: boolean } = {
      params: { variant },
      responseType: 'blob',
      timeout: 90000,
      _handleErrorLocally: true,
    };
    const response = await api.get(`databases/documents/${documentId}/export/pdf`, config);
    return response.data instanceof Blob ? response.data : new Blob([response.data]);
  },

  /**
   * Atualizar documento
   * Rota backend: PUT /api/databases/documents/:id
   */
  async updateDocument<T = unknown>(documentId: string, data: Partial<T>): Promise<DatabaseDocumentResponse<T>> {
    const response = await api.put(`databases/documents/${documentId}`, { data }, HANDLE_ERROR_LOCALLY);
    return response.data.data;
  },

  /**
   * Deletar documento
   * Rota backend: DELETE /api/databases/documents/:id
   */
  async deleteDocument(documentId: string): Promise<void> {
    await api.delete(`databases/documents/${documentId}`);
  }
};
